import React, { useContext, useEffect, useRef, useState } from "react";
import { CheckMultiSelect, GridLoader } from "../../rcsgrid/createGridTools";
import { formEdit, updateGridData } from "../../Tools/Tools";
import {
  GetAddressUseKladr,
  GetDialogParamsForSearch,
  getTableLayout,
  handleTable,
  saveBookState,
} from "../../Tools/Requests";
import Tree from "../../Windows/ViewData/Tree/Tree";
import { parseColumns } from "../../rcsgrid/GridTools/parseColumnTools";
import LoadingMask from "../../NotWorkArea(Side&Head)/LoadingMask";
import { TokenJson, tokenProcessingTest } from "../../../TokenProcessing/TokenProcessing";
import { getSmetaTabContent } from "./Components/DialogDetailsTools";
import DialogHeaderButtons from "./Components/DialogHeaderButtons";
import DialogMultiSplitContent from "./Components/DialogMultiSplitContent";
import MainTableContent from "./Components/MainTableContent";
import SmetaTabs from "../../Tools/Tabs/SmetaTabs";
import { DefaultDialogContentProps } from "./DialogAcctools";
import { SmetaModalsButtonsContext, SmetaModalsIdContext } from "../../Tools/modalManager";
import { BufferElement, GridDataBuffer } from "./DialogNFA";
import { createGrid, createRecordSource } from "../../rcsgrid/rcsgrid";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";
import {
  createGridStateString,
  parseGridState,
} from "../../Sections/ElementsSections/SectionDocuments/DocumentsConsts";
import { SmetaTabItem } from "../../Tools/Tabs/PinIcons";
import { Prefixes } from "../../Tools/ProjectX";
import smetaAxios from "../../../SmetaAxios";
import { IconButton } from "@mui/material";
import { HTMLTreeUListElement } from "../../../ComponentInterface";
import { createModalLeaveScope, createSectionLeaveScope, useLeaveEffect } from "../../Tools/leaveGuard";

interface Page {
  Caption: string;
  CLSID?: string;
  Module?: Prefixes;
  Params?: { prm?: string; Path?: string; ProgID?: string; ObjType?: string };
  Token?: string;
  ObjType?: string;
}

/**
 * Возвращает данные о группе с указанным objRef
 * @param {Object} [data] данные для диалога
 * @param {Prefixes} [data.module] модуль объекта
 * @param {string} [data.objType] тип объекта
 * @param {string} [data.objRef] id искомой записи
 * @returns Возвращает объект с id группы привязанной к записи
 */
const getDialogParams = async (data: {
  module: Prefixes;
  objType?: string;
  objRef?: string;
}): Promise<{ NodeID?: string }> => {
  const params = {
    ObjType: data.objType,
    ObjRef: data.objRef,
  };
  const config = {
    prefix: data.module,
    method: "GET",
    url: "GetDialogParams",
    params,
  };
  return smetaAxios(config);
};

/**
 * Возвращает данные об основной организации
 * @returns Возвращает объект с id записи и группой основной организации
 */
const getMainOrg = (): Promise<{ ID: string; GroupID?: string }> => {
  const params = {};
  const config = {
    prefix: Prefixes.orgs,
    method: "GET",
    url: "GetMainOrg",
    params,
  };
  return smetaAxios(config);
};

/**
 * Возвращает данные для контекстного меню
 * @param {Object} [data]
 * @param {Prefixes} [data.module] модуль объекта
 * @param {string} [data.objType] тип объекта
 * @param {string} [data.objRef] id искомой записи
 * @param {string} [data.selection] выбранные записи
 * @param {string} [data.sectionId] id секции
 * @returns Возвращает набор опций для контекстного меню
 */
const bookContextMenuHandler = (data: {
  module: Prefixes;
  id: string;
  objRef?: string;
  objType: string;
  selection?: string;
  sectionId?: string;
}): Promise<TokenJson> => {
  const params = {
    ID: data.id,
    ObjRef: data.objRef,
    ObjType: data.objType,
    Selection: data.selection,
    WSM: "1",
    SectionID: data.sectionId,
  };
  const config = {
    prefix: data.module,
    method: "GET",
    url: "BookContextMenuHandler",
    params,
  };
  return smetaAxios(config);
};

/**
 * Запрос сохранения состояния таблицы
 *
 * @param {Object} [data] объект с данными для запроса
 * @param {Prefixes} [data.module] модуль, в котором находится
 * @param {string} [data.sectionId] id секции или модального окна
 * @param {string} [data.objType] тип объекта таблицы
 * @param {string} [data.mainGrid] объект таблицы
 * @param {Object} [data.statesData] объект состояний
 *
 * @returns Promise<{}>
 */
const saveGridState = async (data: {
  sectionId?: string;
  objType?: string;
  mainGrid?: { grid: createGrid; source: createRecordSource };
  statesData?: { [key: string]: any };
}) => {
  if (!data.mainGrid) return;
  const state: { [key: string]: any } = {};
  if (data.statesData) {
    Object.entries(data.statesData).forEach(([key, value]) => {
      state[`${key}`] = { $content: createGridStateString(value) };
    });
  }
  const params = {
    SectionID: data.sectionId,
    ObjType: data.objType,
  };
  const config = {
    prefix: Prefixes.orgs,
    method: "POST",
    url: "SaveGridState",
    data: { State: state },
    params,
  };

  return smetaAxios(config);
};

/**
 * Загружает последнее состояние таблицы
 * @returns Возвращает объект состояния таблицы
 */
const loadGridState = () => {
  const params = {};
  const config = {
    prefix: Prefixes.orgs,
    method: "GET",
    url: "LoadBookState",
    params,
  };
  return smetaAxios(config);
};

/**
 * Возвращает id картинки для записи по значению
 * @param value значение поля
 * @param field название поля
 * @returns Возвращает индекс картинки
 */
export const orgGetImage = (value: number | string, field: string) => {
  if (field === "CloseDate") {
    return Number(value) > 0 ? 154 : 0;
  }

  if (Number(value) > 0) {
    return 54;
  }

  if (value === "-1") {
    return 3;
  }

  return -value;
};

/**
 * Контент диалога Организации
 *
 * Поля компонента:
 *
 *    1. mainBlock = useRef<any>(null);
 *    2. gridPanel - контейнер для таблицы
 *    3. gridDataBuffer - объект с кэшем диалога
 *    4. pages - объект с данными вкладок текущей группы
 *    5. selectNewId - найти запись в родительской группе
 *    6. tabIndex - текущий индекс вкладки
 *    7. treeRef - ссылка на дерево
 *    8. gridStates - ссылка на дерево
 *    9. [mainGrid, setMainGrid] - объект с экземплярами классов управления таблицей
 *    10. [selectedRecordID, setSelectedRecordID] - выбранная запись в таблице
 *    11. [groupID, setGroupID] - выбранная группа в дереве
 *    12. [accSelectedRecordID, setAccSelectedRecordID] - выбранная запись в таблице счетов
 *    13. [countRecords, setCountRecords] - количество записей в текущей таблице
 *    14. [searchResult, setSearchResult] - запрос поиска записи
 *    15. [tabs, setTabs] - вкладки
 *    16. [needUpdate, setNeedUpdate] - необходимость обновить текущую вкладку
 *    17. [timer, setTimer] - таймер для задержки обновления
 *    18. [kladr, setKladr] - состояние классификатора адресов
 *    19. [collapsedDetails, setCollapsedDetails] - состояние видимости детализации
 *    20. [showDetails, setShowDetails] - показать детализацию
 *    21. [collapsedTree, setCollapsedTree] - состояние вдимости дерева
 *    22. [showTree] - показать дерево
 *
 * @param {Object} [props] объект с данными для компонента подразделений
 * @param {Prefixes} [props.Module] модуль компонента
 * @param {string} [props.ObjType] тип объекта для таблицы
 * @param {string} [props.ObjRef] id выбранной по умолчанию записи
 * @param {string} [props.NodeID] id выбранной по умолчанию группы
 * @param {string} [props.CLSID] id компоненты НФА
 * @param {string} [props.SectionID] id текущей секции
 * @param {string} [props.Selector] селектор таблицы
 * @param {string} [props.RequestID] id запроса для цепи запросов
 * @param {string} [props.Buttons] настрока видимых кнопок
 * @param {string} [props.hiddenButton] скрыть панель с кнопками
 * @param {string} [props.record] данные параметров для параметров
 * @param {string} [props.from] данные для цепи запросов
 * @param {Object} [props.addInfo] дополнительные данные
 * @param {string} [props.onEdit] функция для кнопки "выбрать"
 * @param {string} [props.setValue] функция обновления состояния внтреннего значения редактора
 * @param {string} [props.setInputValue] функция обновлнния состояния значения редактора
 * @param {string} [props.func] функция для цепи запросов
 *
 * @returns Возвращает контент диалога Организации
 */
export default function DialogOrganizations({ props }: DefaultDialogContentProps) {
  const {
    Module: module,
    ObjType: objType,
    ObjRef: objRef,
    NodeID: nodeId,
    CLSID: clsid,
    SectionID: sectionId,
    Selector: selector,
    RequestID: requestId,
    Buttons: buttons,
    hiddenButton,
    record,
    from,
    Name: name,
    addInfo,
    onEdit,
    setValue,
    setInputValue,
    func,
  } = props;

  const mainBlock = useRef<any>(null);
  const gridPanel = useRef(null);
  const gridDataBuffer = useRef<GridDataBuffer>({});
  const pages = useRef<{ [key: string]: Array<Page> }>({});
  const selectNewId = useRef<string>(undefined);
  const tabIndex = useRef(0);
  const treeRef = useRef<HTMLTreeUListElement>(null);
  const gridStates = useRef<{ [key: string]: any }>({});

  const [mainGrid, setMainGrid] = useState<{ grid: createGrid; source: createRecordSource }>();
  const [selectedRecordID, setSelectedRecordID] = useState<string | undefined>(objRef);
  const [groupID, setGroupID] = useState<string>(nodeId || "0");
  const [accSelectedRecordID, setAccSelectedRecordID] = useState<string>();
  const [countRecords, setCountRecords] = useState<number>(0);
  const [searchResult, setSearchResult] = useState<{ id: string; groupId?: string }>();

  const [tabs, setTabs] = useState<any>(null);
  const [needUpdate, setNeedUpdate] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [kladr, setKladr] = useState<boolean>();

  const [collapsedDetails, setCollapsedDetails] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [collapsedTree, setCollapsedTree] = useState(false);
  const [showTree] = useState(true);

  const setButtons = useContext(SmetaModalsButtonsContext);

  useEffect(() => {
    if (hiddenButton) return;
    const modalButtons: { [key: number]: { label: string; onClick?: () => void } } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = { label: "Выбрать", onClick: onSelect };
    }
    setButtons?.(modalButtons);
  }, [setButtons, mainGrid, mainBlock, selectedRecordID, accSelectedRecordID]);

  /**
   * Инициализация классификатора адресов
   */
  const initAddressParams = () => {
    async function getKladr() {
      const json = await GetAddressUseKladr();
      setKladr(json.service === "1");
    }
    getKladr();
  };
  useEffect(initAddressParams, []);

  /**
   * Инициализация таблицы
   */
  const initGrid = () => {
    loadGrid();
  };
  useEffect(initGrid, []);

  const modalId = useContext(SmetaModalsIdContext);
  const leaveScope = modalId ? createModalLeaveScope(modalId) : createSectionLeaveScope(sectionId);
  useLeaveEffect({
    id: `grid-state:${objType}:${sectionId}`,
    scope: leaveScope,
    enabled: Boolean(mainGrid),
    run: async () => {
      await saveGridState({ sectionId, objType, mainGrid, statesData: gridStates.current });
    },
  });

  /**
   * Обновление данных при изменении выбранной группы в дереве
   */
  const updateGroup = () => {
    changeGroup();
  };
  useEffect(updateGroup, [groupID, mainGrid]);

  /**
   * Обновление данных при изменении текущей записи
   */
  const onRecChange = () => {
    if (mainGrid !== undefined && gridDataBuffer.current[groupID]) {
      gridDataBuffer.current[groupID].selectedId = selectedRecordID;
    }

    if (mainGrid !== undefined) {
      updateSmetaTab(tabIndex.current);
    }
  };
  useEffect(onRecChange, [selectedRecordID]);

  /**
   * Сохранение состояния вкладок для текущей группы
   */
  const saveTabsData = () => {
    if (gridDataBuffer.current[groupID]) gridDataBuffer.current[groupID].tabs = tabs;
  };
  useEffect(saveTabsData, [tabs]);

  /**
   * Сохранение выбранной вкладки в текущей группе
   */
  const saveTabData = () => {
    if (gridDataBuffer.current[groupID]) gridDataBuffer.current[groupID].tabIndex = tabIndex.current;
  };
  useEffect(saveTabData, [tabIndex.current]);

  /**
   * Обновление вкладки по необходимости
   */
  const updateTab = () => {
    if (needUpdate) {
      updateSmetaTab(tabIndex.current);
      setNeedUpdate(false);
    }
  };
  useEffect(updateTab, [needUpdate]);

  /**
   * Поиск записи в справочнике
   */
  const findRecord = () => {
    if (searchResult) {
      searchItem(searchResult);
    }
  };
  useEffect(findRecord, [searchResult]);

  /**
   * Загружает основную таблицу и сохраненяет ее в кэш
   */
  const loadGrid = async () => {
    if (mainGrid) return;
    const states = await loadGridState();
    if (states) {
      gridStates.current = {};

      Object.entries(states).forEach(([key, value]) => {
        if (value.content) gridStates.current[key] = parseGridState(value.content);
      });
    }

    const json = await getTableLayout<any>({ module: module, objType: "0", groupId: groupID, sectionId: sectionId });
    if (json?.Details) {
      const defaultTabs = getDefaultTabsData();
      const serverTabs = json.Details?.items || [];
      pages.current[groupID] = [...serverTabs, ...defaultTabs];
    }

    gridDataBuffer.current[groupID] = {
      columns: parseColumns(json, { collapsibleField: "Name" }),
      selectedId: objRef,
      bufferObjType: json.ObjType,
    };

    setMainGrid(
      await GridLoader(gridPanel.current, props, {
        setSelectedRecord: setSelectedRecordID,
        json,
        setCountRecords,
        groupID,
        main: true,
      }),
    );
  };

  /**
   * Обновляет таблицу и вкладки при смене группы
   */
  const changeGroup = async () => {
    if (mainGrid) {
      const state = gridStates.current[`группа ${groupID}`];
      if (state) {
        mainGrid.source.setGridState(state);
      }
      await refreshGrid();
    }

    if (pages.current[groupID]) {
      await getSmetaTabs();
      setTimeout(() => {
        setNeedUpdate(true);
      });
    }
  };

  /**
   * Ищет и фокусируется на запись с указанным по группам и таблице
   */
  const searchItem = async (searchData: { id: string; groupId?: string }) => {
    const id = searchData.groupId || GetDialogParamsForSearch(module, objType, searchData.id)?.NodeID;

    if (id && id !== groupID) {
      await treeRef.current?.refreshTree(id, { ObjRef: id });
      return;
    }
    mainGrid?.grid.refreshSource(searchData.id);
  };

  /**
   * Обновляет текущую таблицу
   */
  const refreshGrid = async () => {
    if (gridDataBuffer.current[groupID]) {
      await getSource(gridDataBuffer.current[groupID]);
      return;
    }
    const json = await getTableLayout<any>({ module: module, objType: "0", groupId: groupID, sectionId: sectionId });
    if (json?.Details) {
      const defaultTabs = getDefaultTabsData();
      const serverTabs = json.Details?.items || [];
      pages.current[groupID] = [...serverTabs, ...defaultTabs];
    }
    gridDataBuffer.current[groupID] = {
      columns: parseColumns(json, { collapsibleField: "Name" }),
      bufferObjType: json.ObjType,
    };

    await getSource(gridDataBuffer.current[groupID]);
  };

  /**
   * Создает новый source если он не был закэширован
   * @param {BufferElement}[data]
   * @param {Array<any>}[data.columns] колонки таблицы
   * @param {string}[data.selectedId] выбранная запись в таблице
   * @param {string}[data.bufferObjType] тип объекта таблицы
   */
  const getSource = async (data: BufferElement) => {
    if (!mainGrid) return;
    const { columns, selectedId, bufferObjType } = data;

    mainGrid.grid.setColumns(columns);

    mainGrid.source.onHandleRequest = handleTable({
      Module: module,
      ObjType: bufferObjType,
      GroupID: groupID,
      selectedRecordID: selectedId || "0",
      SectionID: sectionId,
    });

    mainGrid.source.onRecordCollapsed = async function (event: any) {
      const recordIndex = event.currentTarget !== undefined ? event.currentTarget.getAttribute("recordindex") : event;
      await smetaAxios.get("RecordCollapsed", {
        prefix: module,
        params: { SectionID: sectionId, ObjType: bufferObjType, RecordIndex: recordIndex },
        loadMask: true,
      });
      await mainGrid.grid.refreshSource("!");
    };

    await mainGrid.grid.setSource(mainGrid.source);

    let refreshID = selectedId || "0";

    if (searchResult) {
      refreshID = searchResult.id;
      setSearchResult(undefined);
    }

    if (selectNewId.current) {
      refreshID = selectNewId.current;
      selectNewId.current = undefined;
    }

    await mainGrid.grid.refreshSource(refreshID);
    setSelectedRecordID(mainGrid.source.getFieldTextSync("ID"));
    await mainGrid.grid.updateGridSize();

    setCountRecords(mainGrid.source.recordCount);
  };

  /**
   * Загружает вкладки и для первой активной подгружает контент
   */
  const getSmetaTabs = async () => {
    if (pages.current[groupID].length === 0) return;
    if (gridDataBuffer.current[groupID]?.tabs) {
      setTabs(gridDataBuffer.current[groupID]?.tabs);
      return;
    }
    const propsFirstTab = getPropsForSmetaTab(pages.current[groupID][0]);
    const firstTab = getSmetaTabContent(propsFirstTab, 0);
    const newTabs = pages.current[groupID].map((page, index) => {
      if (index === 0) return firstTab;
      return (
        <SmetaTabItem key={`${page.Caption}-${index}`} label={page.Caption}>
          <LoadingMask />
        </SmetaTabItem>
      );
    });
    setTabs(newTabs);
    setShowDetails(true);
  };

  /**
   * Возвращает дефолтные вкладки
   * @returns Возвращает вкладки по умолчанию для любой группы
   */
  const getDefaultTabsData = () => {
    return [{ Caption: "Поиск", CLSID: "Search" }];
  };

  /**
   * Обновляет контент при смене вкладки
   *
   * @param {number} index id текущей вкладки
   */
  const onTabChange = async (index: number, element: any, data: any, actualIndex: any) => {
    tabIndex.current = index;
    updateSmetaTab(index);
  };

  /**
   * Обновляет вкладку по указанному id
   *
   * @param {number} index id текущей вкладки
   */
  const updateSmetaTab = async (index: number) => {
    if (!tabs) return;
    if (collapsedDetails) return;
    clearTimeout(timer);
    const newTimer = setTimeout(async () => {
      const newTabProps = getPropsForSmetaTab(pages.current[groupID][index]);
      tabs[index] = getSmetaTabContent(newTabProps, index);
      setTabs([...tabs]);
    }, 50);
    setTimer(newTimer);
  };

  /**
   * Возвращает новые сойства для детализации
   * @param page данные вкладки
   * @returns Возвращает объект со свойствами вкладки
   */
  const getPropsForSmetaTab = (page: Page) => {
    return {
      ...page,
      rights: mainGrid?.source?.rights,
      module: page.Module || module,
      groupId: groupID,
      objType: page.ObjType || page.Params?.ObjType,
      selectedRecordID: selectedRecordID,
      setAccSelectedRecordID: setAccSelectedRecordID,
      setSearchResult: (id: string) => setSearchResult({ id }),
      kladr: kladr,
    };
  };

  /**
   * Фокусируется на основную организацию
   */
  const getMainOrganization = async () => {
    const json = await getMainOrg();
    if (groupID !== json.GroupID) {
      setSearchResult({ id: json.ID, groupId: json.GroupID });
      return;
    }
    setSearchResult({ id: json.ID });
  };

  /**
   * Устанавливает выбранное значение
   */
  const onSelect = async () => {
    if (!mainGrid) return;
    saveBookState(module);

    let value;
    if (selector === "Счета") {
      const accBox = mainBlock.current?.querySelector("#accBox");
      const accGrid = accBox?.querySelector("#rcgrid_panel");
      value = accGrid?.grid.source.getFieldTextSync("Name");
    } else {
      const shortName = mainGrid.source.getFieldTextSync("ShortName");
      const cellFullName = mainGrid.source.getFieldTextSync("FullName");
      const cellName = mainGrid.source.getFieldTextSync("Name");

      value = `${shortName ? `(${shortName})` : ""} ${cellFullName}`;
      if (value === " ") {
        value = cellName;
      }
    }

    if (requestId) {
      await formEdit(
        "1",
        func,
        {
          Name: "ObjRef",
          Value: selector === "Счета" ? accSelectedRecordID : selectedRecordID,
        },
        requestId,
        from,
      );
      return;
    }

    onEdit?.({
      value: value.trim(),
      tag: selector === "Счета" ? accSelectedRecordID : selectedRecordID,
      name: name,
      record: record,
      textchanged: "0",
      setValue: setValue,
      setInputValue: setInputValue,
      type: module,
      requestId: requestId,
      addInfo: addInfo,
    });
  };

  /**
   * Возвращает массив выбранных записей, если они есть
   * @returns Возвращает массив записей отмеченных множественным выбором
   */
  const getSelection = async () => {
    if (!mainGrid) return;
    if (mainGrid.grid.getMultiSelect().length !== 0) {
      return await CheckMultiSelect(mainGrid.grid, "ID", ",");
    }
  };

  /**
   * Исполняет стандартную функцию при клике на опцию контекстного меню
   * @param {Object} [data]
   * @param {string} [data.IDfix] id программы опции
   * @param {string} [data.Caption] имя опции
   */
  const defaultContextClick = async (data: { IDfix?: string; Caption: string }) => {
    if (!mainGrid) return;
    if (data.IDfix !== undefined) {
      const selection = await getSelection();

      const json = await bookContextMenuHandler({
        module,
        id: data.IDfix,
        objRef: selectedRecordID,
        objType: gridDataBuffer.current[groupID].bufferObjType,
        selection: selection,
      });
      if (json.Token) {
        tokenProcessingTest(json, {
          func: () => mainGrid.grid.refreshSource(selectedRecordID),
        });
      } else await mainGrid.grid.refreshSource(selectedRecordID);
    }
    if (data.Caption === "Основная организация") getMainOrganization();
  };

  /**
   * Обрабатывает клики на элементы контекстного меню
   * @param data данные об элементе
   */
  const contextMenuHandler = (data: any) => {
    const comands: { [key: string]: (data?: any) => void | Promise<void> } = {
      ShowGroupID: showGroupID,
    };

    comands[data.value]?.(data) || defaultContextClick(data);
  };

  /**
   * Фокусируется на группу с выбранной записью
   */
  const showGroupID = async () => {
    if (!mainGrid) return;
    const json = await getDialogParams({ module: module, objType: objType, objRef: selectedRecordID });
    if (!(json && json.NodeID)) return;
    if (selectedRecordID && json.NodeID !== groupID) {
      setSearchResult({ id: selectedRecordID, groupId: json.NodeID });
      return;
    }
    if (selectedRecordID) mainGrid.grid.refreshSource(selectedRecordID);
  };

  /**
   * Обновляет данные опции
   * @param val предыдущее значение опции
   * @returns Возвращает новые данные для опции
   */
  const changeDataOfItem = (val: { value: string }) => {
    const changeValue: { [key: string]: (data: any) => any } = {};
    return changeValue[val.value]?.(val) || val;
  };

  /**
   * Скрывает или показывает детализацию
   */
  const onCollapseDetatils = () => {
    if (collapsedDetails) setNeedUpdate(true);
    setCollapsedDetails(!collapsedDetails);
    onResize();
  };
  /**
   * Скрывает или показывает дерево
   */
  const onCollapseTree = () => {
    setCollapsedTree(!collapsedTree);
    onResize();
  };

  /**
   * Позиционируется на вкладку поиска
   */
  const onClickSearch = () => {
    if (tabs) {
      tabIndex.current = tabs.length - 1;
      if (collapsedDetails) {
        onCollapseDetatils();
      } else setNeedUpdate(true);
    }
  };

  /**
   * Обновляет таблицу при изменении размеров
   */
  const onResize = () => {
    updateGridData(0);
  };

  return (
    <div ref={mainBlock} className={clsx(dialogStyles.mainDialogBlock)}>
      <DialogHeaderButtons
        buttons={{ leftSplit: true, bottomSplit: true, search: true, grouping: true }}
        funcs={{
          leftSplit: onCollapseTree,
          bottomSplit: onCollapseDetatils,
          search: onClickSearch,
        }}
        disabled={{}}
      />
      <DialogMultiSplitContent
        mainContent={
          <MainTableContent
            Module={module}
            SectionID={sectionId}
            contextMenuModule="organizations"
            gridPanelRef={gridPanel}
            mainGrid={mainGrid}
            selectedRecordID={selectedRecordID}
            objType={objType}
            contextMenuHandler={contextMenuHandler}
            changeDataOfItem={changeDataOfItem}
          />
        }
        showBottom={showDetails}
        collapsedBottom={collapsedDetails}
        bottomContent={
          <SmetaTabs position="bottom" tabIndex={tabIndex.current} children={tabs} onTabIndexChange={onTabChange} />
        }
        showLeft={showTree}
        collapsedLeft={collapsedTree}
        leftContent={
          <div className={clsx(dialogStyles.mainDialogBlock)}>
            <div className={clsx(dialogStyles.fullDialogFlexContainer)}>
              <Tree
                props={{
                  ref: treeRef,
                  CLSID: clsid,
                  multiCheck: false,
                  ObjType: objType,
                  SectionID: sectionId,
                  Module: module,
                  Current: groupID,
                  ObjRef: groupID,
                  selectID: (id: string) => {
                    const state = mainGrid?.source?.saveState();
                    if (state) {
                      gridStates.current[`группа ${groupID}`];
                    }
                    setGroupID(id);
                  },
                  UsedDate: "0",
                  Info: "0",
                  Internal: "0",
                }}
              />
            </div>
            <div className={clsx(dialogStyles.dialogOrganizationsMainOrgButtonContainer)}>
              <IconButton className={clsx(dialogStyles.dialogOrganizationsMainOrgButton)} onClick={getMainOrganization}>
                Основная организация
              </IconButton>
            </div>
          </div>
        }
        onResizeEndBottom={onResize}
        onResizeEndLeft={onResize}
      />
      <div className={clsx(dialogStyles.dialogAddInfoContainer)}>Количество записей: {countRecords || 0}</div>
    </div>
  );
}
